import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Award, Users, TrendingUp, Target } from 'lucide-react';
import { ConvergenceDiagram, ExecutiveSplitSection } from '../components/executive';

// Heritage stats removed - no fabricated data

const About = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Leadership team data (preserved for future use)
  const teamMembers = [
    {
      name: 'Sarah Johnson',
      role: 'CEO & Founder',
      bio: 'With over 15 years in technology leadership, Sarah drives our vision and strategy.',
      image: 'https://randomuser.me/api/portraits/women/32.jpg'
    },
    {
      name: 'David Chen',
      role: 'CTO',
      bio: 'David brings extensive experience in enterprise architecture and AI development.',
      image: 'https://randomuser.me/api/portraits/men/22.jpg'
    },
    {
      name: 'Amina Patel',
      role: 'Head of Data Strategy',
      bio: 'Amina turns enterprise data into governed decision assets.',
      image: 'https://randomuser.me/api/portraits/women/44.jpg'
    },
    {
      name: 'Michael Robinson',
      role: 'Director of AI Advisory',
      bio: 'Michael shapes AI operating models with controls, evidence, and measurable value.',
      image: 'https://randomuser.me/api/portraits/men/32.jpg'
    }
  ];

  const values = [
    {
      icon: <Target className="w-8 h-8 text-blue-500" />,
      title: 'Pragmatism',
      description: 'We favor practical systems that executives can fund, govern, and measure.'
    },
    {
      icon: <Users className="w-8 h-8 text-blue-500" />,
      title: 'Partnership',
      description: 'We work alongside leadership on the decisions that matter—not as an implementation partner.'
    },
    {
      icon: <Award className="w-8 h-8 text-blue-500" />,
      title: 'Accountability',
      description: 'We help leadership clarify ownership, controls, and evidence—without prescribing fixed outcomes.'
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-500" />,
      title: 'Value',
      description: 'We connect AI and data work to operating results.'
    }
  ];

  return (
    <div className={theme === 'dark' ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-700'}>
      {/* Hero */}
      <section className={`${theme === 'dark' ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-br from-white via-blue-50 to-slate-100/60'} py-20 lg:py-28`}>
        <div className="max-w-4xl mx-auto text-center space-y-6 px-6">
          <p className={`text-xs uppercase tracking-[0.4em] ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>About Us</p>
          <h1 className="text-4xl lg:text-6xl font-semibold">AI And Data Advisory For Executive Decisions.</h1>
          <p className="text-lg lg:text-xl text-slate-900 dark:text-slate-200">Shared Oxygen brings board-level advisory and principal engineering judgment to executive decisions on AI and data.</p>
        </div>
      </section>

      {/* Heritage stats section removed - no fabricated data */}

      {/* Narrative */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <ExecutiveSplitSection
            theme={theme}
            accentBar="bg-slate-400"
            title="Why We Exist"
            illustration={<ConvergenceDiagram theme={theme} />}
          >
            <p>
              AI programs fail when strategy, data, architecture, and ownership are treated as separate workstreams.
            </p>
            <p>
              We bring CDO, AI product, and principal engineering judgment to one executive table—so decisions
              are made with the full picture in view, not through handoffs between silos.
            </p>
            <p>
              We stay close from mandate through the decisions that follow so value, control, and evidence remain visible to leadership.
            </p>
          </ExecutiveSplitSection>

          <div className={`mt-16 grid md:grid-cols-2 gap-8 ${theme === 'dark' ? 'bg-slate-900/60 border border-slate-800' : 'bg-white border border-slate-200 shadow-md'} rounded-3xl p-8 lg:p-10`}>
            <div>
              <h3 className="text-xl font-semibold mb-4">Vision</h3>
              <p>AI should become a trusted operating capability, not a side experiment.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Mission</h3>
              <p>Help leaders put AI and data systems under governance, measurement, and financial accountability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className={`${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-100'} py-20`}>
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-center mb-12">Principles We Operate By</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className={`${theme === 'dark' ? 'bg-slate-950/40 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'} rounded-3xl p-6`}>
                <div className="mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-sm text-slate-700 dark:text-slate-200">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section - Hidden for future use
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className={`text-3xl font-bold mb-12 text-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Our Leadership Team
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg overflow-hidden shadow-md`}
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-56 object-cover"
                />
                <div className="p-6">
                  <h3 className={`text-xl font-semibold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {member.name}
                  </h3>
                  <p className="text-blue-500 mb-3">{member.role}</p>
                  <p>{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* CTA */}
      <section className={`${theme === 'dark' ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-900'} py-20`}>
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl lg:text-4xl font-semibold mb-6">Ready To Clarify Your AI And Data Priorities?</h2>
          <p className="text-lg text-white/80 mb-8">Share the mandate and constraints. An initial conversation to understand what counsel would be useful.</p>
          <button
            onClick={() => navigate('/contact')}
            className="px-8 py-3 rounded-full bg-white text-slate-900 font-semibold inline-flex items-center gap-2 mx-auto"
          >
            Get In Touch
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;
